import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions,
} from '@angular/material/form-field';
import { provideRouter } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { provideHighcharts } from 'highcharts-angular';
import { catchError, firstValueFrom, of } from 'rxjs';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { conflictHttpInterceptor } from 'src/app/modules/shared/interceptors/conflict.http.interceptor';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { provideLunaIcons } from './luna-icons';
import { msalConfig } from './modules/auth/auth-config';
import { serverErrorHttpInterceptor } from './modules/shared/interceptors/server-error.http.interceptor';
import { unauthorizedHttpInterceptor } from './modules/shared/interceptors/unauthorized.http.interceptor';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const meScopes = environment.apiConfig.scopes;
  const groupScopes = [...environment.apiConfig.scopes, 'GroupMember.Read.All'];
  const protectedResourceMap = new Map<string, string[]>();

  protectedResourceMap.set(environment.apiConfig.uri, meScopes);
  protectedResourceMap.set(`${environment.apiConfig.uri}/*`, meScopes);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/groups', groupScopes);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/groups/*', groupScopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    strictMatching: true,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/auth/login-failed',
  };
}

function initializeApp(authService: AuthService, msalService: MsalService) {
  return async () => {
    await msalService.instance.initialize();
    const redirectResult = await firstValueFrom(msalService.handleRedirectObservable());

    if (redirectResult?.account) {
      msalService.instance.setActiveAccount(redirectResult.account);
    }

    // Recover the MSAL account silently *before* any protected HTTP call. If
    // MSAL has no account, the JWT exchange below would trigger MsalInterceptor's
    // acquireTokenRedirect mid-boot, leaving the bootstrap in a half-completed
    // state and producing an EmptyError from firstValueFrom.
    // TODO: Potentially remove once JWT logic is removed and MSAL is the single source of truth for auth state.
    if (msalService.instance.getAllAccounts().length === 0) {
      try {
        const result = await firstValueFrom(
          msalService.ssoSilent({ scopes: environment.apiConfig.scopes })
        );
        if (result.account) {
          msalService.instance.setActiveAccount(result.account);
        }
      } catch {
        // No active Microsoft session — skip the JWT call and let the normal
        // login flow take over once routing activates.
        return;
      }
    }

    // catchError + defaultValue: getUserByToken can complete empty (e.g. 401
    // → interceptor returns of(null) → page navigates) or error outright.
    // Bootstrap must never reject on auth-state issues; the unauthorized
    // interceptor and the login route handle the user-facing flow.
    await firstValueFrom(authService.getUserByToken().pipe(catchError(() => of(undefined))), {
      defaultValue: undefined,
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideLunaIcons(),
    provideHighcharts({
      instance: () => import('highcharts/esm/highcharts').then((m) => m.default),
    }),
    provideFlatpickrDefaults(),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'always',
        subscriptSizing: 'dynamic',
      } satisfies MatFormFieldDefaultOptions,
    },
    provideHttpClient(
      withInterceptors([
        unauthorizedHttpInterceptor,
        serverErrorHttpInterceptor,
        conflictHttpInterceptor,
      ]),
      withInterceptorsFromDi()
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(AuthService), inject(MsalService));
      return initializerFn();
    }),
  ],
};
